/**
 * Single derivation of the user's persona, from `usePatient()` +
 * `useActiveCycleWithPhases()`. Replaces ad-hoc reads of
 * `patient.gender`, `patient.fertilityIntent.goal`, etc. scattered across
 * screens.
 *
 * v1 persona model is binary AFAB / AMAB based on sex at birth. Trans /
 * non-binary handling is not part of v1 (per the launch plan).
 *
 * Use this hook wherever a screen branches on persona — e.g., gender-aware
 * Find Care defaults (1.7), Testing forWhom filtering (1.8), MedicalHistory
 * gendered sections (1.9), Costs sperm-freezing path (1.10), homepage
 * routing (1.4).
 */
import { usePatient } from "../patients/hooks/usePatient";
import { getPatientGoal } from "../patients/patients.types";
import { useActiveCycleWithPhases } from "../cycles/hooks/useCycles";
import { DEV_PERSONA_OVERRIDE } from "./devPersonaOverride";
import {
  resolveActiveJourneyRole,
  type JourneyRole,
} from "./resolveActiveJourneyRole";

export type PersonaGender = "AFAB" | "AMAB" | "unknown";

export type PersonaPrimaryIntent =
  "preserve" | "grow" | "donate" | "learn" | "unknown";

export type PersonaJourneyStage =
  | "explore"
  | "trying-naturally"
  | "before-treatment"
  | "in-care"
  | "time-to-wait"
  | "complete";

export type PersonaPartnerChoice =
  "solo" | "with-partner" | "using-donor" | "unknown";

/**
 * Side of the helper relationship for users on donate / surrogacy paths.
 * Captured in onboarding (HowAreYouHelping screen) and persisted via the
 * patient PATCH. Null for users on preserve / grow-with-own-gametes / learn.
 */
export type PersonaJourneyRole = JourneyRole;

/** Compatibility vocabulary for existing homepage surfaces. */
export type PersonaHelperRole = "intended-parent" | "carrier" | "donor" | null;

export type Persona = {
  gender: PersonaGender;
  primaryIntent: PersonaPrimaryIntent;
  /** Raw fertilityIntent.goal value when present (richer than primaryIntent). */
  treatmentGoal: string | null;
  journeyStage: PersonaJourneyStage;
  partnerChoice: PersonaPartnerChoice;
  /** Canonical role in the selected primary journey. */
  journeyRole: PersonaJourneyRole;
  /** @deprecated Use journeyRole. Kept as a derived UI compatibility value. */
  helperRole: PersonaHelperRole;
  age: number | null;
  conditions: string[];
  /**
   * True when the persona derivation is still loading (patient or active
   * cycle hooks are pending). Consumers can show skeletons while loading,
   * then branch on the persona once ready.
   */
  isLoading: boolean;
};

/* ─── Goal → primaryIntent mapping ─── */

function derivePrimaryIntent(
  goal: string | null | undefined,
  journeyRole: PersonaJourneyRole,
): PersonaPrimaryIntent {
  if (!goal) return "unknown";
  switch (goal) {
    case "exploring":
      return "learn";
    case "egg-freezing":
    case "sperm-freezing":
    case "embryo-freezing":
    case "egg-preserve":
    case "embryo-preserve":
    case "sperm-preserve":
      return "preserve";
    case "pregnancy":
      return "grow";
    case "egg-donation":
    case "sperm-donation":
    case "surrogacy":
      // Donor / carrier roles are providing → donate.
      // Intended-parent role is using a donor / surrogate to grow → grow.
      // Missing role: donations default to donate; surrogacy without role
      // info is rare and falls through.
      if (journeyRole === "donor" || journeyRole === "gestational-carrier") {
        return "donate";
      }
      if (journeyRole === "intended-parent") return "grow";
      if (goal === "egg-donation" || goal === "sperm-donation") {
        return "donate";
      }
      return "unknown";
    case "adoption":
      // Adoption is out of scope for Louise (different ecosystem entirely).
      // Old patient records that have it surface here as the unspecified
      // primary intent value.
      return "unknown";
    default:
      return "unknown";
  }
}

/* ─── Stage derivation ─── */

function deriveJourneyStage(
  fertilityStage: string | null | undefined,
  hasActiveCycle: boolean,
  cycleStatus: string | null | undefined,
): PersonaJourneyStage {
  // Active cycle takes precedence over self-reported stage.
  if (hasActiveCycle) {
    if (cycleStatus === "completed" || cycleStatus === "discontinued") {
      return "complete";
    }
    return "in-care";
  }

  switch (fertilityStage) {
    case "exploring-options":
      return "explore";
    case "getting-ready":
      return "before-treatment";
    case "in-treatment":
      // No active cycle but stage says in-treatment — likely between cycles.
      return "before-treatment";
    case "trying-naturally":
      return "trying-naturally";
    default:
      return "explore";
  }
}

/* ─── Age from DOB ─── */

function ageFromDateOfBirth(dob: string | null | undefined): number | null {
  if (!dob) return null;
  const dobDate = new Date(dob);
  if (Number.isNaN(dobDate.getTime())) return null;
  const ms = Date.now() - dobDate.getTime();
  return Math.floor(ms / 31557600000);
}

/* ─── Hook ─── */

export function useUserPersona(): Persona {
  const patientQuery = usePatient();
  const cycleQuery = useActiveCycleWithPhases();

  const patient = patientQuery.data;
  const intent = patient?.fertilityIntent ?? null;
  const cycle = cycleQuery.cycle;

  // Dev URL overrides win when present — lets design passes preview any
  // persona without going through onboarding. No-op outside __DEV__.
  const realGender: PersonaGender =
    patient?.gender === "AFAB" || patient?.gender === "AMAB"
      ? patient.gender
      : "unknown";
  const gender: PersonaGender = DEV_PERSONA_OVERRIDE.gender ?? realGender;

  // fertilityIntent.goal is the current contract, but older patients may
  // still only have a treatment intent. Keep every persona consumer on the
  // same API-backed resolver so those records do not silently become an
  // unexplained/default journey.
  const realGoal = patient ? getPatientGoal(patient) : null;
  const goal = DEV_PERSONA_OVERRIDE.treatmentGoal ?? realGoal;

  // journeyRole is the canonical API-backed role. Never fall back to a global
  // helperRole field; older records without it use the donation-goal fallback.
  const recordedJourneyRole: PersonaJourneyRole = intent?.journeyRole ?? null;
  const journeyRole: PersonaJourneyRole = recordedJourneyRole
    ? resolveActiveJourneyRole(goal, recordedJourneyRole)
    : goal === "egg-donation" || goal === "sperm-donation"
      ? "donor"
      : null;
  const helperRole: PersonaHelperRole =
    journeyRole === "gestational-carrier" ? "carrier" : journeyRole;

  const derivedIntent = derivePrimaryIntent(goal, journeyRole);
  const primaryIntent = DEV_PERSONA_OVERRIDE.primaryIntent ?? derivedIntent;

  const partnerChoice: PersonaPartnerChoice =
    patient?.partnerChoice === "solo" ||
    patient?.partnerChoice === "with-partner" ||
    patient?.partnerChoice === "using-donor"
      ? patient.partnerChoice
      : "unknown";

  const journeyStage = deriveJourneyStage(
    intent?.stage ?? null,
    !!cycle,
    cycle?.status ?? null,
  );

  const conditions = (patient?.medicalHistory?.diseases ?? [])
    .map((d) => d.disease ?? "")
    .filter((d) => !!d);
  const age = ageFromDateOfBirth(patient?.dateOfBirth);

  return {
    gender,
    primaryIntent,
    treatmentGoal: goal ?? null,
    journeyStage,
    partnerChoice,
    journeyRole,
    helperRole,
    age,
    conditions,
    isLoading: patientQuery.isLoading || cycleQuery.isLoading,
  };
}
